import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import SimpleMaterialsList from '../panels/SimpleMaterialsList';
import { AudioCatalogTabPane } from './AudioCatalogTabPane';
import { filterAudioCatalogByRole, type AudioCatalogRole } from '../../features/audioCatalog/catalogRoles';
import { useAudioCatalog } from '../../features/audioCatalog/hooks';
import type { AudioCatalogItem } from '../../features/audioCatalog/api';
import { resolveMaterialUrls } from '../../features/materials/materialUrl';
import type { CloudMaterial, TemporaryMaterial } from '../../features/materials/materialsSlice';
import styles from './MaterialsPickerModal.module.css';

// 種別フィルタは既定で「動画」固定 (参照を安定させて初期フィルタの useEffect が
// 毎レンダリングで走らないようにモジュールレベルに置く)
const VIDEO_FILE_TYPES = ['video'];

type Props = {
  /** 素材の選択。false (または false を返す Promise) を返すとモーダルを閉じない (選んだ素材を弾いたとき)。 */
  onSelect: (imageUrl: string, materialId?: string) => void | boolean | Promise<void | boolean>;
  onClose: () => void;
  title?: string;
  /** デフォルトで適用するアスペクト比フィルタ (シナリオのアスペクト比) */
  aspectRatio?: string;
  /** デフォルトで適用するブランドフィルタ (シナリオのブランドが判明している場合) */
  brandId?: string | null;
  /** 一覧から除外する素材ID (既に紐付け済みの素材を隠す用途) */
  excludeMaterialIds?: string[];
  /** 絞り込む素材種別。既定は ['video']。音声ピッカーでは ['audio'] を渡す */
  fileTypes?: string[];
  /** サウンドロゴタグ付き素材を最上段に優先表示する (音声ピッカー用) */
  soundLogoFirst?: boolean;
  /** 一覧の優先順位 (大きいほど上・同点はサーバー順)。スクロール動画の背景ピッカーが「構図が合う素材」を上に出すのに使う */
  prioritize?: (material: CloudMaterial | TemporaryMaterial) => number;
  /** 取得元フィルタを出す (既定は非表示)。音声ピッカーで BGM / ナレーション等に絞りたいときに true */
  showSourceTypeFilter?: boolean;
  /** 取得元フィルタの初期値 (例: BGM ピッカーで ['bgm'])。ユーザーは変更可 */
  initialSourceTypes?: string[];
  /** アスペクト比フィルタを隠す (音声ピッカーなど比率が意味を持たないとき) */
  hideAspectRatioFilter?: boolean;
  /** カードのファイル容量バッジを出さない */
  hideFileSizeBadge?: boolean;
  /**
   * 音声ピッカー専用: 「提供ライブラリ」タブを足し、運営 / パートナー提供の効果音・フリー楽曲
   * (audio_catalog_items) のうち role の用途のものを出す。行のクリックで onSelect を呼び、
   * true を返したら閉じる (取り込み = 素材への複製は呼び出し側が行う)。
   */
  /** ナレーション追加ピッカー: フィルタに「シナリオ」を出し、このシナリオ (開いているシナリオ) で絞り込んだ状態で始める */
  narrationScenarioId?: string;
  catalog?: {
    role: AudioCatalogRole;
    onSelect: (item: AudioCatalogItem) => boolean | Promise<boolean>;
    /** 提供ライブラリタブを開いた状態で始める */
    openFirst?: boolean;
  };
};

/**
 * 共有の素材一覧コンポーネント (SimpleMaterialsList) を select モードでモーダル表示する
 * 素材ピッカー。ブランドカット(タイトル/エンドカード)の動画素材選択に使う。
 * - 種別は「動画」に固定し、種別フィルタ自体は非表示
 * - 取得元フィルタは既定で非表示 (showSourceTypeFilter で表示)
 * - アスペクト比はシナリオのアスペクト比をデフォルト選択 (ユーザーは変更可)
 * - 動画ピッカー時は各カードでインライン再生可 (選択前に中身を確認できる)
 * - 「詳細」ボタンは出さず、常時表示の「選択」ボタンで確定する
 * - 音声だけのピッカー (fileTypes=['audio']) はカードではなく一覧テーブルで出し、行のクリックで確定する
 * - 音声 + 動画のピッカー (fileTypes=['audio', 'video']) は、表示中が音声だけならテーブル、動画が混ざればカード
 * - catalog を渡すと「提供ライブラリ」タブ (運営 / パートナー提供の効果音・フリー楽曲) を足す
 */
export function MaterialsPickerModal({ onSelect, onClose, title = '素材を選択', aspectRatio, brandId, excludeMaterialIds, fileTypes, soundLogoFirst, prioritize, showSourceTypeFilter = false, initialSourceTypes, hideAspectRatioFilter = false, hideFileSizeBadge = false, narrationScenarioId, catalog }: Props) {
  const initialAspectRatios = useMemo(
    () => (aspectRatio ? [aspectRatio] : undefined),
    [aspectRatio],
  );
  // 音声など他種別のときは参照を安定させて useEffect 再実行を防ぐ
  const initialFileTypes = useMemo(
    () => (fileTypes && fileTypes.length ? fileTypes : VIDEO_FILE_TYPES),
    [fileTypes],
  );
  // 取得元の初期値も参照を安定させる
  const stableInitialSourceTypes = useMemo(
    () => (initialSourceTypes && initialSourceTypes.length ? initialSourceTypes : undefined),
    [initialSourceTypes],
  );
  // 動画ピッカーのときだけブランドカット素材を先頭に寄せる
  const isVideoPicker = initialFileTypes === VIDEO_FILE_TYPES;
  // 音声だけのピッカーは、音声選択モーダル共通の一覧テーブル (AudioTrackTable) で出す
  const isAudioPicker = initialFileTypes.length > 0 && initialFileTypes.every((t) => t === 'audio');
  // 音声 + 動画のピッカー (効果音ピッカーで動画の音声を取り出すとき) は、表示中が音声だけならテーブル、
  // 動画が混ざればカード (動画はサムネイルで見分ける・カード上で再生して音を確かめられる)
  const isAudioOrVideoPicker = !isAudioPicker && initialFileTypes.includes('audio')
    && initialFileTypes.every((t) => t === 'audio' || t === 'video');

  // モーダル表示中は背景スクロールを無効化
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // シナリオ絞り込み (参照を安定させる。ナレーション追加ピッカーでは開いているシナリオ ID が無くても出す)
  const narrationScenarioFilter = useMemo(
    () => (narrationScenarioId !== undefined ? { initialScenarioId: narrationScenarioId || undefined } : undefined),
    [narrationScenarioId],
  );

  // 提供ライブラリタブの件数 (用途で絞った数)
  const { data: catalogItems } = useAudioCatalog(!!catalog);
  const catalogCount = catalog && catalogItems ? filterAudioCatalogByRole(catalogItems, catalog.role).length : null;

  const handleSelect = (material: CloudMaterial | TemporaryMaterial) => {
    const urls = resolveMaterialUrls(material);
    const url = urls.fullsize || urls.source || material.preview_url || material.thumbnail_url;
    if (url) {
      const result = onSelect(url, material.id);
      if (result instanceof Promise) {
        void result.then((ok) => { if (ok !== false) onClose(); });
      } else if (result !== false) {
        onClose();
      }
    }
  };

  const handleCatalogSelect = async (item: AudioCatalogItem) => {
    if (!catalog) return;
    if (await catalog.onSelect(item)) onClose();
  };

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={(e) => {
        // 親モーダル (AddSceneModal 等) のオーバーレイへバブリングして閉じてしまうのを防ぐ
        e.stopPropagation();
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>{title}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>
        <div className={styles.body}>
          <SimpleMaterialsList
            mode="select"
            hideFileTypeFilter
            hideSourceTypeFilter={!showSourceTypeFilter}
            hideAspectRatioFilter={hideAspectRatioFilter}
            hideFileSizeBadge={hideFileSizeBadge}
            hideStoryboardTab
            hideAmazonComplianceFilter
            initialFileTypes={initialFileTypes}
            initialAspectRatios={initialAspectRatios}
            initialSourceTypes={stableInitialSourceTypes}
            selectedBrandId={brandId ?? undefined}
            onSelectMaterial={handleSelect}
            showMaxDurationFilter
            alwaysShowSearch
            brandCutFirst={isVideoPicker}
            playableVideoCards={isVideoPicker || isAudioOrVideoPicker}
            hideDetailsButton
            showSelectButton
            soundLogoFirst={soundLogoFirst}
            prioritize={prioritize}
            excludeMaterialIds={excludeMaterialIds}
            audioTableLayout={isAudioPicker || (isAudioOrVideoPicker ? 'auto' : false)}
            // 生成したナレーションは個人フォルダに入るので、ナレーション追加ピッカーは個人フォルダから開く
            initialTab={catalog?.openFirst ? 'catalog' : narrationScenarioFilter ? 'temporary' : undefined}
            narrationScenarioFilter={narrationScenarioFilter}
            audioTableNameLabel={narrationScenarioFilter ? 'ナレーション原稿' : undefined}
            catalogTab={catalog ? {
              label: '提供ライブラリ',
              shortLabel: '提供',
              count: catalogCount,
              content: <AudioCatalogTabPane role={catalog.role} onSelect={handleCatalogSelect} />,
            } : undefined}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
